import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TablePagination,
} from '@mui/material';
import {
  CheckCircle as SettledIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useAccountingSummary, useAuth } from '../../hooks';
import { useDocumentTitle, usePagination } from '../../hooks/core';
import { PlayerAvatar, PageHeader } from '../../components/shared';
import { AccountingEducationModal } from '../../components/education';
import { hasSeenEducationModal } from '../../lib/educationModals';
import type { AggregatedUserBalance, CashFlowStats } from '../../types';

type BalanceFilter = 'all' | 'owes' | 'owed' | 'settled';
type CashFlowInterval = '7days' | '30days' | 'thisYear' | 'allTime';

// Helper to get display name for a balance
const getDisplayName = (balance: AggregatedUserBalance): string => {
  if (balance.isOfflinePlayer && balance.offlinePlayer) {
    return `${balance.offlinePlayer.displayName} (offline)`;
  }
  return balance.user?.displayName || 'Unknown';
};

// Helper to get interval label
const getIntervalLabel = (interval: CashFlowInterval): string => {
  switch (interval) {
    case '7days': return 'Last 7 Days';
    case '30days': return 'Last 30 Days';
    case 'thisYear': return 'This Year';
    case 'allTime': return 'All Time';
  }
};

export const AccountingPage = () => {
  useDocumentTitle('Accounting');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { summary, isLoading, error } = useAccountingSummary();
  const [filter, setFilter] = useState<BalanceFilter>('all');
  const [cashFlowInterval, setCashFlowInterval] = useState<CashFlowInterval>('30days');
  const { paginationProps, getPaginatedItems, resetPage } = usePagination({ rowsPerPageOptions: [10, 20, 50, 100] });
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('accounting'));

  // Filter out the current user from the balances list (managers don't owe themselves)
  const allBalances = useMemo(() => {
    if (!summary?.balances) return [];
    return summary.balances.filter(balance => {
      // Don't show the current user in the list
      if (!balance.isOfflinePlayer && balance.userId === user?.id) {
        return false;
      }
      return true;
    });
  }, [summary?.balances, user?.id]);

  // Get cash flow data for selected interval
  const getCashFlowForInterval = (): CashFlowStats | null => {
    if (!summary) return null;
    switch (cashFlowInterval) {
      case '7days': return summary.cashFlow7Days;
      case '30days': return summary.cashFlow30Days;
      case 'thisYear': return summary.cashFlowThisYear;
      case 'allTime': return summary.cashFlowAllTime;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary || summary.completedBoardsCount === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="Accounting"
          helpLink="/support/accounting"
          helpTooltip="Learn more about accounting"
        />
        <Alert severity="info">
          You don't have any completed boards yet. Accounting is only available for completed boards.
          Once you complete a board by recording all quarter scores, you'll be able to track payments here.
        </Alert>
      </Box>
    );
  }

  // Apply filter
  // "Owes Money" and "Owed Money" filters exclude settled users
  const filteredBalances = allBalances.filter(balance => {
    switch (filter) {
      case 'owes':
        return balance.netBalance < 0 && !balance.fullySettled;
      case 'owed':
        return balance.netBalance > 0 && !balance.fullySettled;
      case 'settled':
        return balance.fullySettled;
      default:
        return true;
    }
  });

  // Calculate counts for filter badges (exclude settled from owes/owed counts)
  const owesCount = allBalances.filter(b => b.netBalance < 0 && !b.fullySettled).length;
  const owedCount = allBalances.filter(b => b.netBalance > 0 && !b.fullySettled).length;
  const settledCount = allBalances.filter(b => b.fullySettled).length;

  // Sort balances: unsettled first (by absolute balance), then settled
  const sortedBalances = [...filteredBalances].sort((a, b) => {
    // Unsettled first
    if (a.fullySettled !== b.fullySettled) {
      return a.fullySettled ? 1 : -1;
    }
    // Then by absolute net balance (highest first)
    return Math.abs(b.netBalance) - Math.abs(a.netBalance);
  });

  const handleRowClick = (balance: AggregatedUserBalance) => {
    if (balance.isOfflinePlayer && balance.offlinePlayerId) {
      navigate(`/accounting/offline/${balance.offlinePlayerId}`);
    } else if (balance.userId) {
      navigate(`/accounting/user/${balance.userId}`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Accounting"
        subtitle="Track payments and winnings for players across your completed boards."
        helpLink="/support/accounting"
        helpTooltip="Learn more about accounting"
      />

      {/* Summary Stats */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" color="text.secondary">Completed Boards</Typography>
            <Typography variant="h6">{summary.completedBoardsCount}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">To Collect</Typography>
            <Typography variant="h6" color="error.main">
              ${summary.totalOwedToManager.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">To Pay Out</Typography>
            <Typography variant="h6" color="success.main">
              ${summary.totalOwedByManager.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Total Players</Typography>
            <Typography variant="h6">{allBalances.length}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Cash Flow Section */}
      {(() => {
        const cashFlow = getCashFlowForInterval();
        if (!cashFlow) return null;
        return (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6">Cash Flow</Typography>
              <ToggleButtonGroup
                value={cashFlowInterval}
                exclusive
                onChange={(_, value) => value && setCashFlowInterval(value)}
                size="small"
              >
                <ToggleButton value="7days">7 Days</ToggleButton>
                <ToggleButton value="30days">30 Days</ToggleButton>
                <ToggleButton value="thisYear">This Year</ToggleButton>
                <ToggleButton value="allTime">All Time</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: 'success.main' }} />
                <Box>
                  <Typography variant="overline" color="text.secondary">Received</Typography>
                  <Typography variant="h6" color="success.main">
                    ${cashFlow.paymentsReceived.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon sx={{ color: 'error.main' }} />
                <Box>
                  <Typography variant="overline" color="text.secondary">Paid Out</Typography>
                  <Typography variant="h6" color="error.main">
                    ${cashFlow.winningsPaidOut.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Net Cash Flow</Typography>
                <Typography
                  variant="h6"
                  color={cashFlow.netCashFlow >= 0 ? 'success.main' : 'error.main'}
                >
                  {cashFlow.netCashFlow >= 0 ? '+' : '-'}${Math.abs(cashFlow.netCashFlow).toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {getIntervalLabel(cashFlowInterval)}
            </Typography>
          </Paper>
        );
      })()}

      {/* Quick Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`All (${allBalances.length})`}
          onClick={() => { setFilter('all'); resetPage(); }}
          color={filter === 'all' ? 'primary' : 'default'}
          variant={filter === 'all' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Owes Money (${owesCount})`}
          onClick={() => { setFilter('owes'); resetPage(); }}
          color={filter === 'owes' ? 'error' : 'default'}
          variant={filter === 'owes' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Owed Money (${owedCount})`}
          onClick={() => { setFilter('owed'); resetPage(); }}
          color={filter === 'owed' ? 'success' : 'default'}
          variant={filter === 'owed' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Settled (${settledCount})`}
          onClick={() => { setFilter('settled'); resetPage(); }}
          color={filter === 'settled' ? 'info' : 'default'}
          variant={filter === 'settled' ? 'filled' : 'outlined'}
        />
      </Box>

      {sortedBalances.length === 0 ? (
        <Typography color="text.secondary">
          No players match the "{filter}" filter
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Player</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 80 }}>Boards</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Owed</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Paid</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Winnings</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 120 }}>Net Balance</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 80 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedItems(sortedBalances)
                  .map((balance) => {
                    const key = balance.isOfflinePlayer
                      ? `offline-${balance.offlinePlayerId}`
                      : `user-${balance.userId}`;
                    return (
                      <tr
                        key={key}
                        onClick={() => handleRowClick(balance)}
                        style={{
                          backgroundColor: balance.fullySettled ? '#e8f5e9' : '#fff',
                          borderBottom: '1px solid #e0e0e0',
                          cursor: 'pointer',
                        }}
                      >
                        {/* Player Name */}
                        <td style={{ padding: '12px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlayerAvatar
                              displayName={balance.isOfflinePlayer ? balance.offlinePlayer?.displayName || '' : balance.user?.displayName || ''}
                              handle={balance.isOfflinePlayer ? undefined : balance.user?.handle}
                              iconName={balance.isOfflinePlayer ? balance.offlinePlayer?.iconName : balance.user?.iconName}
                              size="small"
                              showBadge={false}
                            />
                            <Typography variant="body2" fontWeight="bold">
                              {getDisplayName(balance)}
                            </Typography>
                          </Box>
                        </td>

                        {/* Board Count */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <Typography variant="body2">
                            {balance.boardCount}
                          </Typography>
                        </td>

                        {/* Total Owed */}
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          <Typography variant="body2" color="text.secondary">
                            ${balance.totalOwed.toFixed(2)}
                          </Typography>
                        </td>

                        {/* Total Paid */}
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          <Typography variant="body2" color="success.main" fontWeight={balance.totalPaid > 0 ? 'bold' : 'normal'}>
                            ${balance.totalPaid.toFixed(2)}
                          </Typography>
                        </td>

                        {/* Total Winnings */}
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          <Typography variant="body2" color="info.main" fontWeight={balance.totalEarned > 0 ? 'bold' : 'normal'}>
                            ${balance.totalEarned.toFixed(2)}
                          </Typography>
                        </td>

                        {/* Net Balance */}
                        <td style={{ textAlign: 'right', padding: '12px' }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={balance.netBalance >= 0 ? 'success.main' : 'error.main'}
                          >
                            {balance.netBalance >= 0 ? '+' : '-'}${Math.abs(balance.netBalance).toFixed(2)}
                          </Typography>
                        </td>

                        {/* Status */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          {balance.fullySettled ? (
                            <Tooltip title="Fully settled">
                              <SettledIcon sx={{ color: 'success.main' }} />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="warning.main">
                              Pending
                            </Typography>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </Box>
          <TablePagination
            component="div"
            count={sortedBalances.length}
            {...paginationProps}
          />
        </>
      )}

      <AccountingEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />
    </Box>
  );
};

export default AccountingPage;
